import React, { useState, useEffect } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { useDancerAuth } from './hooks/useDancerAuth';
import { useProfileClaimed } from './hooks/useProfileClaimed';
import './ClaimProfile.css';

export default function ClaimProfile({ dancerName, onClaimSubmitted }) {
  const { user, claimStatus, claimData, loading } = useDancerAuth(dancerName);
  const [showForm, setShowForm] = useState(false);
  const { isProfileClaimed, claimedBy } = useProfileClaimed(dancerName);
  const [submitting, setSubmitting] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentFileBack, setDocumentFileBack] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [message, setMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [storageAvailable, setStorageAvailable] = useState(null);
  
  // Check if document type requires front and back
  const requiresFrontBack = documentType === 'id';

  // Test storage availability on component mount
  useEffect(() => {
    const testStorageAccess = async () => {
      if (!user) return;
      
      try {
        console.log('🔍 Testing Firebase Storage availability...');
        console.log('📦 Storage config:', {
          bucket: storage.app.options.storageBucket,
          projectId: storage.app.options.projectId
        });
        
        // Force token refresh before testing storage
        try {
          const { auth } = await import('./firebase');
          if (auth.currentUser) {
            await auth.currentUser.getIdToken(true);
            console.log('✅ Auth token refreshed for storage test');
          }
        } catch (tokenError) {
          console.error('❌ Failed to refresh token for storage test:', tokenError);
        }
        
        // Create a minimal test file
        const testBlob = new Blob(['test'], { type: 'text/plain' });
        const testRef = ref(storage, `test/${user.uid}/test.txt`);
        
        try {
          const uploadTask = uploadBytesResumable(testRef, testBlob);
          
          uploadTask.on('state_changed', 
            (snapshot) => {
              console.log('✅ Storage test upload progress:', snapshot.bytesTransferred, '/', snapshot.totalBytes);
            },
            (error) => {
              console.error('❌ Storage test upload error:', error);
              setStorageAvailable(false);
              setMessage(`❌ Storage test failed: ${error.message}. Please refresh the page and try again.`);
            },
            async () => {
              try {
                console.log('✅ Storage test upload completed successfully');
                setStorageAvailable(true);
                setMessage('✅ Ready to submit profile claim');
              } catch (urlError) {
                console.error('❌ Error getting download URL:', urlError);
                setStorageAvailable(false);
                setMessage(`❌ Storage test failed: ${urlError.message}. Please refresh the page and try again.`);
              }
            }
          );
        } catch (taskError) {
          console.error('❌ Failed to create upload task:', taskError);
          setStorageAvailable(false);
          setMessage(`❌ Cannot create upload task: ${taskError.message}. Please refresh the page. If the issue persists, Storage may not be enabled.`);
        }
        
      } catch (error) {
        console.error('❌ Storage not available:', error);
        setStorageAvailable(false);
        setMessage('⚠️ Firebase Storage is not available. Please refresh the page and try again.');
      }
    };
    
    if (user) {
      testStorageAccess();
    }
  }, [user]);

  const calculateAgeCategory = (birthDate) => {
    const today = new Date();
    // Parse dd/mm/yyyy format
    const [day, month, year] = birthDate.split('/').map(num => parseInt(num));
    const birth = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    // Adjust age if birthday hasn't occurred this year
    const adjustedAge = (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) 
      ? age - 1 
      : age;
    
    if (adjustedAge <= 12) return 'Children';
    if (adjustedAge <= 15) return 'Juniors I';
    if (adjustedAge <= 18) return 'Juniors II';
    if (adjustedAge <= 21) return 'Youth';
    if (adjustedAge <= 35) return 'Adults';
    return 'Seniors';
  };

  const validateBirthDate = (dateString) => {
    const pattern = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/[0-9]{4}$/;
    if (!pattern.test(dateString)) return false;
    
    const [day, month, year] = dateString.split('/').map(num => parseInt(num));
    const date = new Date(year, month - 1, day);
    
    // Check if the date is valid and not in the future
    return date.getDate() === day && 
           date.getMonth() === month - 1 && 
           date.getFullYear() === year &&
           date <= new Date();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setMessage('❌ You must be logged in to claim a profile');
      return;
    }

    if (isProfileClaimed) {
      setMessage('❌ This profile has already been claimed');
      return;
    }

    if (claimStatus === 'pending') {
      setMessage('⏳ You already have a pending claim for this profile');
      return;
    }

    if (claimStatus === 'approved') {
      setMessage('✅ Your claim for this profile has already been approved');
      return;
    }

    if (!documentFile) {
      setMessage('❌ Please upload a document for verification');
      return;
    }

    if (requiresFrontBack && !documentFileBack) {
      setMessage('❌ Please upload both front and back side of your document');
      return;
    }

    if (!birthDate) {
      setMessage('❌ Please fill in all required fields');
      return;
    }

    if (!validateBirthDate(birthDate)) {
      setMessage('❌ Please enter a valid birth date in dd/mm/yyyy format');
      return;
    }

    if (storageAvailable === false) {
      setMessage('❌ Storage is not available. Please refresh the page and try again.');
      return;
    }

    setSubmitting(true);
    setMessage('🔐 Refreshing authentication...');

    // Force token refresh before upload to prevent auth issues on mobile
    try {
      const { auth } = await import('./firebase');
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
        console.log('✅ Auth token refreshed before upload');
      }
    } catch (tokenError) {
      console.error('❌ Failed to refresh token:', tokenError);
      setMessage('❌ Authentication error. Please log out and log back in.');
      setSubmitting(false);
      return;
    }

    setMessage('📤 Uploading document(s) and submitting claim...');

    try {
      // Create organized folder structure: verification-docs/DancerName/DancerName_timestamp.ext
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19); // Format: 2024-01-15T14-30-45
      const sanitizedDancerName = dancerName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
      
      // Helper function to convert image to JPEG
      const convertToJpeg = async (file) => {
        // If it's a PDF, don't convert
        if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
          return file;
        }
        
        // For images, convert to JPEG for better compatibility
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              // Create canvas and draw image
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.fillStyle = '#FFFFFF'; // White background for transparency
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0);
              
              // Convert to JPEG blob
              canvas.toBlob((blob) => {
                if (blob) {
                  // Create new file with .jpg extension
                  const baseName = file.name.replace(/\.[^/.]+$/, '');
                  const newFile = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
                  console.log(`✅ Converted ${file.name} to JPEG (${(blob.size / 1024).toFixed(1)} KB)`);
                  resolve(newFile);
                } else {
                  reject(new Error('Failed to convert image to JPEG'));
                }
              }, 'image/jpeg', 0.9); // 90% quality
            };
            img.onerror = () => reject(new Error('Failed to load image for conversion'));
            img.src = e.target.result;
          };
          reader.onerror = () => reject(new Error('Failed to read file for conversion'));
          reader.readAsDataURL(file);
        });
      };
      
      // Helper function to upload a file
      const uploadFile = async (file, suffix = '') => {
        // Convert image to JPEG first (handles HEIC, PNG, etc.)
        const processedFile = await convertToJpeg(file);
        const fileExtension = processedFile.name.split('.').pop();
        const fileName = `${sanitizedDancerName}_${timestamp}${suffix}.${fileExtension}`;
        const documentRef = ref(storage, `verification-docs/${sanitizedDancerName}/${fileName}`);
        
        return new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(documentRef, processedFile);
          
          uploadTask.on('state_changed', 
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
              console.log(`📤 Upload progress: ${progress.toFixed(1)}% - ${fileName}`);
            },
            (error) => {
              console.error('Upload error:', error);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                console.log(`✅ Document uploaded successfully: ${fileName}`);
                console.log(`📁 Storage location: verification-docs/${sanitizedDancerName}/${fileName}`);
                resolve({
                  url: downloadURL,
                  path: `verification-docs/${sanitizedDancerName}/${fileName}`,
                  fileName: fileName
                });
              } catch (urlError) {
                reject(urlError);
              }
            }
          );
        });
      };
      
      // Upload front side (always required)
      setMessage('📤 Uploading front side...');
      const frontResult = await uploadFile(documentFile, requiresFrontBack ? '_front' : '');
      
      // Upload back side (if required)
      let backResult = null;
      if (requiresFrontBack && documentFileBack) {
        setMessage('📤 Uploading back side...');
        backResult = await uploadFile(documentFileBack, '_back');
      }
      
      // Submit claim to Firestore
      const ageCategory = calculateAgeCategory(birthDate);
      
      const claimData = {
        dancerName,
        userId: user.uid,
        userEmail: user.email,
        birthDate,
        ageCategory,
        documentType,
        documentURL: frontResult.url,
        documentPath: frontResult.path,
        documentFileName: frontResult.fileName,
        status: 'pending',
        submittedAt: Timestamp.now()
      };
      
      // Add back side info if applicable
      if (backResult) {
        claimData.documentURLBack = backResult.url;
        claimData.documentPathBack = backResult.path;
        claimData.documentFileNameBack = backResult.fileName;
      }
      
      await addDoc(collection(db, 'profileClaims'), claimData);

      setMessage('✅ Profile claim submitted successfully! We will review it soon.');
      
      // Reset form
      setDocumentFile(null);
      setDocumentFileBack(null);
      setDocumentType('');
      setBirthDate('');
      setUploadProgress(0);
      
      // Notify parent component
      if (onClaimSubmitted) {
        onClaimSubmitted();
      }

    } catch (error) {
      console.error('Error in claim submission:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="claim-profile-loading">Loading...</div>;

  if (!user) {
    return (
      <div className="claim-profile-container">
        <div className="claim-profile-message">
          <h3>🔐 Login Required</h3>
          <p>You must be logged in to claim a dancer profile.</p>
        </div>
      </div>
    );
  }

  if (isProfileClaimed) {
    return (
      <div className="claim-profile-container">
        <div className="claim-profile-message">
          <h3>✅ Profile Already Claimed</h3>
          <p>This profile has been claimed by {claimedBy?.email || 'another user'}.</p>
        </div>
      </div>
    );
  }

  if (claimStatus === 'approved') {
    return (
      <div className="claim-profile-container">
        <div className="claim-profile-message success">
          <h3>🎉 Claim Approved!</h3>
          <p>Your claim for this profile has been approved. You can now edit the profile information.</p>
        </div>
      </div>
    );
  }

  if (claimStatus === 'pending') {
    return (
      <div className="claim-profile-container">
        <div className="claim-profile-message">
          <h3>⏳ Claim Pending Review</h3>
          <p>Your claim for this profile is currently under review. We'll notify you once it's processed.</p>
        </div>
      </div>
    );
  }

  if (claimStatus === 'rejected' && !showForm) {
    return (
      <div className="claim-profile-container">
        <div className="claim-profile-message error">
          <h3>❌ Claim Rejected</h3>
          <p>Your previous claim for this profile was rejected.</p>
          {claimData?.rejectionReasons && claimData.rejectionReasons.length > 0 && (
            <div style={{ marginTop: '1rem', textAlign: 'left', backgroundColor: '#fff5f5', padding: '1rem', borderRadius: '8px', border: '1px solid #f5c6cb' }}>
              <strong>Reason(s):</strong>
              <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                {claimData.rejectionReasons.map((reason, idx) => (
                  <li key={idx} style={{ color: '#721c24' }}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
          <button
            onClick={() => setShowForm(true)}
            style={{
              marginTop: '1.5rem',
              padding: '0.75rem 1.5rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            🔄 Apply Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="claim-profile-container">
      <h3>🎭 Claim Profile: {dancerName}</h3>
      <p className="claim-profile-intro">
        To claim this dancer profile, please provide verification information. All claims are manually reviewed.
      </p>

      <form onSubmit={handleSubmit} className="claim-profile-form">
        <div className="form-group">
          <label htmlFor="birthDate">Birth Date * (dd/mm/yyyy)</label>
          <input
            type="text"
            id="birthDate"
            value={birthDate}
            onChange={(e) => {
              let value = e.target.value.replace(/[^0-9]/g, ''); // Remove non-digits
              
              // Auto-format with slashes
              if (value.length >= 2) {
                value = value.slice(0, 2) + '/' + value.slice(2);
              }
              if (value.length >= 5) {
                value = value.slice(0, 5) + '/' + value.slice(5, 9);
              }
              
              setBirthDate(value);
            }}
            required
            placeholder="01/01/2000"
            pattern="^(0[1-9]|[12][0-9]|3[01])/(0[1-9]|1[0-2])/[0-9]{4}$"
            title="Please enter date in dd/mm/yyyy format"
            maxLength="10"
          />
        </div>

        <div className="form-group">
          <label htmlFor="documentType">Verification Document Type *</label>
          <select
            id="documentType"
            value={documentType}
            onChange={(e) => {
              setDocumentType(e.target.value);
              // Reset file inputs when document type changes
              setDocumentFile(null);
              setDocumentFileBack(null);
            }}
            required
          >
            <option value="">Select document type</option>
            <option value="id">Government ID</option>
            <option value="passport">Passport</option>
            <option value="birth_certificate">Birth Certificate</option>
          </select>
        </div>

        {documentType && (
          <>
            <div className="form-group">
              <label htmlFor="documentFile">
                {requiresFrontBack ? 'Upload Front Side *' : 'Upload Verification Document *'}
              </label>
              <input
                type="file"
                id="documentFile"
                onChange={(e) => setDocumentFile(e.target.files[0])}
                accept="image/*,.pdf"
                required
              />
              {documentFile && (
                <small className="file-selected">✓ {documentFile.name}</small>
              )}
            </div>

            {requiresFrontBack && (
              <div className="form-group">
                <label htmlFor="documentFileBack">Upload Back Side *</label>
                <input
                  type="file"
                  id="documentFileBack"
                  onChange={(e) => setDocumentFileBack(e.target.files[0])}
                  accept="image/*,.pdf"
                  required
                />
                {documentFileBack && (
                  <small className="file-selected">✓ {documentFileBack.name}</small>
                )}
              </div>
            )}

            <small className="file-help">
              Accepted: Images (JPG, PNG) or PDF. Max 10MB per file.
              {requiresFrontBack && ' Please upload both front and back sides of your document.'}
            </small>
          </>
        )}

        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <span>{Math.round(uploadProgress)}% uploaded</span>
          </div>
        )}

        {message && (
          <div className={`claim-message ${
            message.includes('✅') ? 'success' : 
            message.includes('❌') ? 'error' : 
            message.includes('⏳') ? 'pending' : ''
          }`}>
            {message}
          </div>
        )}

        <button 
          type="submit" 
          disabled={submitting || storageAvailable === false}
          className="submit-claim-btn"
        >
          {submitting ? '📤 Submitting...' : '🎭 Submit Claim'}
        </button>
      </form>

      <div className="claim-profile-note">
        <p><strong>Note:</strong> Profile claims are manually reviewed to ensure authenticity. Please provide accurate information to speed up the review process.</p>
      </div>
    </div>
  );
}
